'use client'

import { useMemo } from 'react'

import { useQueries } from '@tanstack/react-query'

import { useAccessToken } from '@b-stock/fe-core/bstock-next/components'
import { interpUri } from '@b-stock/fe-core/bstock-react'
import type { SavedSearchDropdownItem } from '@b-stock/fe-core/bstock-react'

import savedSearchCountQuery, {
  getSavedSearchCount,
} from '@/queries/savedSearchCountQuery'
import { findAllQuery } from '@/queries/savedSearchQuery'

// Show the buyer's most recent saved searches in the dropdown; "See all
// searches" opens the full modal.
const MAX_DISPLAYED_SEARCHES = 3
const FIND_ALL_LIMIT = 100

type SavedSearchesDropdownData = {
  items: SavedSearchDropdownItem[]
  isLoading: boolean
}

/**
 * Sources the buyer's saved searches (via the find-all query — the
 * `SavedSearchContext` provider isn't mounted at header scope) and adapts them
 * into fe-core `SavedSearchDropdownItem`s, fetching a per-search result count
 * for the "N results" pill (same `savedSearchCountQuery` the homepage module
 * uses).
 */
export function useSavedSearchesDropdownData(
  accountId: string | undefined,
  personId: string | undefined
): SavedSearchesDropdownData {
  const accessToken = useAccessToken()

  const { data, isLoading } = findAllQuery.useWithOpts(
    [
      {
        accountId: accountId ?? '',
        personId: personId ?? '',
        limit: FIND_ALL_LIMIT,
      },
    ],
    { enabled: !!accountId && !!personId }
  )

  const savedSearches = useMemo(() => data?.savedSearches ?? [], [data])
  const displayedSearches = useMemo(
    () => savedSearches.slice(0, MAX_DISPLAYED_SEARCHES),
    [savedSearches]
  )

  const enableCounts = !!accessToken && displayedSearches.length > 0

  const countQueries = useQueries({
    queries: enableCounts
      ? displayedSearches.map((search) => ({
          queryKey: savedSearchCountQuery.getKey(accessToken, [search]),
          queryFn: () => getSavedSearchCount(accessToken, search),
          staleTime: Infinity,
          retry: 2,
          refetchOnWindowFocus: false,
          refetchOnReconnect: false,
          refetchOnMount: false,
        }))
      : [],
  })

  const items = useMemo<SavedSearchDropdownItem[]>(
    () =>
      displayedSearches.map((search, index) => {
        const count = countQueries[index]?.data
        return {
          id: search._id,
          label: search.name,
          ...(typeof count === 'number' ? { resultsCount: count } : {}),
          href: interpUri`/all-auctions?savedSearchId=${search._id}`,
        }
      }),
    [displayedSearches, countQueries]
  )

  return {
    items,
    isLoading,
  }
}
